import { DesignData } from 'editor/src/store/design/types';
import getLayoutSchemaByName from 'editor/src/store/editor/selector/getLayoutSchemaByName';
import { RootState } from 'editor/src/store/index';
import getResizableProductForGivenVariant from 'editor/src/store/variants/selector/getResizableProductForGivenVariant';
import getVariationGroupsFromDesignOptions from 'editor/src/store/variants/selector/getVariationGroupsFromDesignOptions';
import { VariationGroup } from 'editor/src/store/variants/types';

import applyResizableProductToDesign from 'editor/src/util/design/applyResizableProductToDesign';
import applyLayoutPerProductUid from 'editor/src/util/layouts/applyLayoutPerProductUid';
import reflectDesignData from 'editor/src/util/reflectDesignData';
import getReflectContext from 'editor/src/util/reflectDesignData/getReflectContext';

import { getVariantDesignKey } from './getGroupDesignKey';

import type { i18n } from 'i18next';

export type GroupDesignUpdate = {
  designData: DesignData | undefined;
  // GAPI-47583 — the group could not follow the active design because its
  // template has not arrived yet: its current designData is now out of date.
  missingTemplate?: boolean;
};

/**
 * Computes the design a variation group should hold so that it follows the
 * active design (`state.design.designData`). Shared by `designListener` (on
 * every design edit) and `applyTemplatesIfNeededOperation` (when design
 * templates arrive) so both propagation paths apply the SAME design — the one
 * the customer is looking at (GAPI-47583).
 */
const getNewDesignDataForGroup = (group: VariationGroup, state: RootState, i18n: i18n): GroupDesignUpdate => {
  const { designData } = state.design;
  const { selectedGroupKey, designTemplates } = state.variants;
  const reflectContext = getReflectContext(state);

  if (group.key === selectedGroupKey) {
    return { designData };
  }

  // The group's design targets its FIRST variant's template — on a group waiting
  // for a newly selected size that size sorts first, and the design must follow
  // it rather than the older variant that still holds the pre-edit design.
  // (Deliberately different from applyTemplatesIfNeededOperation's
  // "does this group have a design?" variant — see getGroupRepresentativeVariant.)
  const designKey = getVariantDesignKey(group.variationsInfo[0], state.variants.selectedPageCount);
  const template = designTemplates[designKey];
  // GAPI-39157: skip reflection when the active design has a different spread
  // count than the target template (e.g. editing a 2-spread design while a
  // 1-spread variant exists). Reflecting across spread counts would either
  // drop spreads or produce empty ones — return undefined to keep the
  // variant's existing designData unchanged.
  if (template && designData && template.spreads.length !== designData.spreads.length) {
    return { designData: undefined };
  }
  if (!template && designData) {
    return { designData: undefined, missingTemplate: true };
  }
  let newDesignData = template && designData ? reflectDesignData(designData, template, reflectContext) : undefined;
  if (newDesignData) {
    newDesignData = applyLayoutPerProductUid(newDesignData, state, true, i18n);

    const resizableElement = getResizableProductForGivenVariant(state, newDesignData.product_uid);
    const layoutName = newDesignData?.spreads[0].layoutSchemaName;
    const layout = layoutName ? getLayoutSchemaByName(state, layoutName) : undefined;

    if (resizableElement && layout && newDesignData.related_dimensions) {
      newDesignData = applyResizableProductToDesign(state, newDesignData, resizableElement, layout, i18n);
    }
  }

  return { designData: newDesignData };
};

/**
 * The keys of the groups that must follow the active design. `undefined` when
 * design options are not in play, in which case every linked group follows.
 */
export const getSelectedDesignOptionGroupKeys = (state: RootState): string[] | undefined => {
  const variationGroupsFromDesignOptions = getVariationGroupsFromDesignOptions(state);
  const selectedGroupDesignOption = variationGroupsFromDesignOptions?.find(
    (group) => group.key === state.variants.selectedDesignOptionValue,
  );
  return selectedGroupDesignOption?.value.map((group) => group.key);
};

export default getNewDesignDataForGroup;
